import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { botService, giftScreenshotService, bilibiliBindingService } from '../services';
import BackButton from '../components/BackButton';
import './GiftScreenshot.css';

// 大航海头像框映射 (guard_level: 1=总督, 2=提督, 3=舰长)
const guardFrameMap = {
  1: 'https://i0.hdslb.com/bfs/live/a454275dea465ac15a03f121f0d7edaf96e30bcf.png', // 总督
  2: 'https://i0.hdslb.com/bfs/live/09937c3beb0608e267a50ac3c7125c3f2d709098.png', // 提督
  3: 'https://i0.hdslb.com/bfs/live/80f732943cc3367029df65e267960d56736a82ee.png'  // 舰长
};

// 分段时间输入组件
const SegmentedTimeInput = ({ label, value, onChange }) => {
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  // 解析 datetime-local 格式的值
  const parseValue = (val) => {
    if (!val) return { year: '', month: '', day: '', hour: '', minute: '' };
    const match = val.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (match) {
      return { year: match[1], month: match[2], day: match[3], hour: match[4], minute: match[5] };
    }
    return { year: '', month: '', day: '', hour: '', minute: '' };
  };

  const parsed = parseValue(value);
  const [year, setYear] = useState(parsed.year);
  const [month, setMonth] = useState(parsed.month);
  const [day, setDay] = useState(parsed.day);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);

  // 同步外部值变化
  useEffect(() => {
    const p = parseValue(value);
    setYear(p.year);
    setMonth(p.month);
    setDay(p.day);
    setHour(p.hour);
    setMinute(p.minute);
  }, [value]);

  // 构建 datetime-local 格式并回调
  const buildValue = (y, m, d, h, min) => {
    if (y && m && d && h !== '' && min !== '') {
      const padMonth = m.padStart(2, '0');
      const padDay = d.padStart(2, '0');
      const padHour = h.padStart(2, '0');
      const padMin = min.padStart(2, '0');
      return `${y}-${padMonth}-${padDay}T${padHour}:${padMin}`;
    }
    return '';
  };

  const handleChange = (field, val, maxLen, nextRef) => {
    // 只允许数字
    const numVal = val.replace(/\D/g, '');
    let newYear = year, newMonth = month, newDay = day, newHour = hour, newMinute = minute;

    switch(field) {
      case 'year': newYear = numVal.slice(0, 4); setYear(newYear); break;
      case 'month': newMonth = numVal.slice(0, 2); setMonth(newMonth); break;
      case 'day': newDay = numVal.slice(0, 2); setDay(newDay); break;
      case 'hour': newHour = numVal.slice(0, 2); setHour(newHour); break;
      case 'minute': newMinute = numVal.slice(0, 2); setMinute(newMinute); break;
      default: break;
    }

    // 自动跳转到下一个输入框
    if (numVal.length >= maxLen && nextRef?.current) {
      nextRef.current.focus();
      nextRef.current.select();
    }

    const newValue = buildValue(newYear, newMonth, newDay, newHour, newMinute);
    if (newValue) {
      onChange(newValue);
    }
  };

  return (
    <div className="segmented-time-input">
      <span className="time-label">{label}</span>
      <div className="time-segments">
        <div className="seg-group date-group">
          <input
            ref={yearRef}
            type="text"
            value={year}
            onChange={(e) => handleChange('year', e.target.value, 4, monthRef)}
            placeholder="2026"
            maxLength={4}
            className="seg-input seg-year"
          />
          <span className="seg-dot">/</span>
          <input
            ref={monthRef}
            type="text"
            value={month}
            onChange={(e) => handleChange('month', e.target.value, 2, dayRef)}
            placeholder="01"
            maxLength={2}
            className="seg-input seg-num"
          />
          <span className="seg-dot">/</span>
          <input
            ref={dayRef}
            type="text"
            value={day}
            onChange={(e) => handleChange('day', e.target.value, 2, hourRef)}
            placeholder="01"
            maxLength={2}
            className="seg-input seg-num"
          />
        </div>
        <div className="seg-group time-group">
          <input
            ref={hourRef}
            type="text"
            value={hour}
            onChange={(e) => handleChange('hour', e.target.value, 2, minuteRef)}
            placeholder="00"
            maxLength={2}
            className="seg-input seg-num"
          />
          <span className="seg-colon">:</span>
          <input
            ref={minuteRef}
            type="text"
            value={minute}
            onChange={(e) => handleChange('minute', e.target.value, 2, null)}
            placeholder="00"
            maxLength={2}
            className="seg-input seg-num"
          />
        </div>
        <label className="calendar-btn">
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          📅
        </label>
      </div>
    </div>
  );
};

// 格式化价值显示
const formatValue = (value) => {
  if (!value) return '0';
  const yuan = value / 1000;
  if (yuan >= 10000) {
    return (yuan / 10000).toFixed(1) + 'w';
  }
  return yuan.toFixed(yuan >= 100 ? 0 : 1);
};

// 格式化时间戳
const formatTimestamp = (ts) => {
  if (!ts) return '';
  const date = new Date(ts * 1000);
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 格式化日期时间为 input datetime-local 格式
const formatDateTimeLocal = (ts) => {
  if (!ts) return '';
  const date = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// 解析 datetime-local 为时间戳
const parseDateTimeLocal = (str) => {
  if (!str) return null;
  return new Date(str).getTime() / 1000;
};

// 获取礼物等级样式类（根据价值）
const getGiftLevelClass = (totalCoin) => {
  const value = totalCoin / 1000; // 转换为元
  if (value >= 100) return 'level-gold';
  if (value >= 15) return 'level-red';
  if (value >= 5) return 'level-purple';
  return ''; // 蓝色（默认）
};

function GiftScreenshot() {
  const navigate = useNavigate();
  
  // 绑定状态检查
  const [accessStatus, setAccessStatus] = useState({ loading: true, hasAccess: false, reason: '' });

  // 房间相关
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 场次相关
  const [broadcasts, setBroadcasts] = useState([]);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);

  // 时间范围
  const [timeRangeType, setTimeRangeType] = useState('current'); // current, previous, today, week, month, custom
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // 可选礼物和用户
  const [availableGifts, setAvailableGifts] = useState({ guard: [], blindbox: {}, other: [] });
  const [activeUsers, setActiveUsers] = useState([]);
  const [giftsLoading, setGiftsLoading] = useState(false);

  // 已选择的礼物和用户
  const [selectedGiftIds, setSelectedGiftIds] = useState(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  // 价值范围
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');

  // 排序和显示选项
  const [sortBy, setSortBy] = useState('value');  // 默认按价值排序
  const [sortOrder, setSortOrder] = useState('desc');
  const [mergeGifts, setMergeGifts] = useState(true);
  const [showAvatar, setShowAvatar] = useState(true);  // 默认显示头像
  const [useNickname, setUseNickname] = useState(false);  // 是否使用用户设置的昵称
  const [transparentBg, setTransparentBg] = useState(true);
  const [themeColor, setThemeColor] = useState('#ff6b9d');

  // 礼物数据
  const [gifts, setGifts] = useState([]);
  const [summary, setSummary] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  
  // 用户头像缓存
  const [userFaces, setUserFaces] = useState({});
  // 用户大航海等级缓存
  const [userGuardLevels, setUserGuardLevels] = useState({});

  // 折叠状态
  const [expandedGroups, setExpandedGroups] = useState({
    guard: true,
    blindbox: true,
    other: true
  });

  // 预览区引用
  const previewRef = useRef(null);

  // 检查绑定状态
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await bilibiliBindingService.checkFeatureAccess();
        setAccessStatus({
          loading: false,
          hasAccess: result.hasAccess,
          reason: result.reason || '',
          code: result.code || ''
        });
      } catch (err) {
        console.error('检查访问权限失败:', err);
        // 如果是 403 错误，从响应中获取信息
        if (err.response?.status === 403) {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: err.response.data?.message || '您没有权限使用此功能',
            code: err.response.data?.code || ''
          });
        } else {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: '检查权限失败，请稍后重试'
          });
        }
      }
    };
    checkAccess();
  }, []);

  // 加载房间列表
  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const config = await botService.getBotConfig();
        if (config && config.rooms) {
          const enabledRooms = config.rooms.filter(r => r.enabled);
          setRooms(enabledRooms);
          if (enabledRooms.length > 0) {
            // 使用 uid（主播UID）作为选中值，与 user_rooms 表存储保持一致
            const firstRoom = enabledRooms[0];
            setSelectedRoom(firstRoom.uid);
          }
        }
      } catch (err) {
        console.error('加载房间列表失败:', err);
        setError('加载房间列表失败');
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, []);

  // 加载场次列表
  useEffect(() => {
    if (!selectedRoom) return;

    const loadBroadcasts = async () => {
      try {
        const data = await giftScreenshotService.getBroadcasts(selectedRoom, 10);
        setBroadcasts(data || []);
        
        // 自动选择本场或最近一场
        if (data && data.length > 0) {
          setSelectedBroadcast(data[0]);
          setTimeRangeType('current');
        }
      } catch (err) {
        console.error('加载场次失败:', err);
      }
    };

    loadBroadcasts();
  }, [selectedRoom]);

  // 计算实际时间范围
  useEffect(() => {
    const now = Date.now() / 1000;
    let start = null;
    let end = now;

    switch (timeRangeType) {
      case 'current':
        if (broadcasts.length > 0) {
          start = broadcasts[0].start_time;
          end = broadcasts[0].end_time || now;
        }
        break;
      case 'previous':
        if (broadcasts.length > 1) {
          start = broadcasts[1].start_time;
          end = broadcasts[1].end_time || now;
        } else if (broadcasts.length > 0) {
          start = broadcasts[0].start_time;
          end = broadcasts[0].end_time || now;
        }
        break;
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        start = today.getTime() / 1000;
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        start = weekAgo.getTime() / 1000;
        break;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        monthAgo.setHours(0, 0, 0, 0);
        start = monthAgo.getTime() / 1000;
        break;
      case 'custom':
        start = parseDateTimeLocal(customStartTime);
        end = parseDateTimeLocal(customEndTime) || now;
        break;
      default:
        break;
    }

    setStartTime(start);
    setEndTime(end);
  }, [timeRangeType, broadcasts, customStartTime, customEndTime]);

  // 加载可选礼物和用户
  useEffect(() => {
    if (!selectedRoom || !startTime || !endTime) return;

    const loadAvailable = async () => {
      try {
        setGiftsLoading(true);
        
        const [giftsData, usersData] = await Promise.all([
          giftScreenshotService.getAvailableGifts(selectedRoom, startTime, endTime),
          giftScreenshotService.getActiveUsers(selectedRoom, startTime, endTime)
        ]);

        setAvailableGifts(giftsData || { guard: [], blindbox: {}, other: [] });
        setActiveUsers(usersData || []);

        // 默认全选所有礼物
        const allGiftIds = new Set();
        if (giftsData) {
          giftsData.guard?.forEach(g => allGiftIds.add(g.gift_id));
          Object.values(giftsData.blindbox || {}).forEach(gifts => {
            gifts.forEach(g => allGiftIds.add(g.gift_id));
          });
          giftsData.other?.forEach(g => allGiftIds.add(g.gift_id));
        }
        setSelectedGiftIds(allGiftIds);
        setSelectedUserIds(new Set()); // 默认不筛选用户
      } catch (err) {
        console.error('加载可选项失败:', err);
      } finally {
        setGiftsLoading(false);
      }
    };

    loadAvailable();
  }, [selectedRoom, startTime, endTime]);

  // 加载礼物数据
  const loadGiftData = useCallback(async () => {
    if (!selectedRoom || !startTime || !endTime) return;

    try {
      setDataLoading(true);
      
      const data = await giftScreenshotService.getGifts(selectedRoom, {
        startTime,
        endTime,
        giftIds: selectedGiftIds.size > 0 ? Array.from(selectedGiftIds) : undefined,
        userIds: selectedUserIds.size > 0 ? Array.from(selectedUserIds) : undefined,
        minValue: minValue ? parseFloat(minValue) * 1000 : undefined,
        maxValue: maxValue ? parseFloat(maxValue) * 1000 : undefined,
        sortBy,
        sortOrder,
        mergeGifts,
        useNickname
        // 移除 limit 限制，获取所有礼物
      });

      setGifts(data.gifts || []);
      setSummary(data.summary || {});

      // 加载用户头像和大航海等级
      if (showAvatar && data.gifts && data.gifts.length > 0) {
        const uids = [...new Set(data.gifts.map(g => g.uid).filter(Boolean))];
        if (uids.length > 0) {
          try {
            const [faces, guardLevels] = await Promise.all([
              giftScreenshotService.getUserFaces(uids),
              giftScreenshotService.getUserGuardLevels(selectedRoom, uids)
            ]);
            setUserFaces(prev => ({ ...prev, ...faces }));
            setUserGuardLevels(prev => ({ ...prev, ...guardLevels }));
          } catch (err) {
            console.error('加载用户头像/大航海等级失败:', err);
          }
        }
      }
    } catch (err) {
      console.error('加载礼物数据失败:', err);
    } finally {
      setDataLoading(false);
    }
  }, [selectedRoom, startTime, endTime, selectedGiftIds, selectedUserIds, minValue, maxValue, sortBy, sortOrder, mergeGifts, useNickname, showAvatar]);

  // 时间范围变更后自动加载数据
  useEffect(() => {
    if (startTime && endTime) {
      loadGiftData();
    }
  }, [loadGiftData, startTime, endTime]);

  // 切换礼物选择
  const toggleGift = (giftId) => {
    setSelectedGiftIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(giftId)) {
        newSet.delete(giftId);
      } else {
        newSet.add(giftId);
      }
      return newSet;
    });
  };

  // 切换整组选择
  const toggleGroup = (groupType, groupName = null) => {
    let groupGifts = [];
    
    if (groupType === 'guard') {
      groupGifts = availableGifts.guard || [];
    } else if (groupType === 'blindbox' && groupName) {
      groupGifts = availableGifts.blindbox?.[groupName] || [];
    } else if (groupType === 'other') {
      groupGifts = availableGifts.other || [];
    }

    const groupIds = groupGifts.map(g => g.gift_id);
    const allSelected = groupIds.every(id => selectedGiftIds.has(id));

    setSelectedGiftIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        groupIds.forEach(id => newSet.delete(id));
      } else {
        groupIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  // 全选/取消全选礼物
  const selectAllGifts = () => {
    const allGiftIds = new Set();
    availableGifts.guard?.forEach(g => allGiftIds.add(g.gift_id));
    Object.values(availableGifts.blindbox || {}).forEach(gifts => {
      gifts.forEach(g => allGiftIds.add(g.gift_id));
    });
    availableGifts.other?.forEach(g => allGiftIds.add(g.gift_id));
    setSelectedGiftIds(allGiftIds);
  };

  const deselectAllGifts = () => {
    setSelectedGiftIds(new Set());
  };

  // 切换用户选择
  const toggleUser = (uid) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uid)) {
        newSet.delete(uid);
      } else {
        newSet.add(uid);
      }
      return newSet;
    });
  };

  // 通过后端代理获取图片的base64
  const imageToBase64ViaProxy = async (url) => {
    try {
      // 已经是 data: URL 的直接返回
      if (url.startsWith('data:')) {
        return url;
      }
      
      // 获取认证 token
      const token = localStorage.getItem('token');
      
      // 通过后端代理获取
      const response = await fetch(`/api/gift-screenshot/proxy-image?url=${encodeURIComponent(url)}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        console.warn('代理请求失败:', response.status, url);
        return null;
      }
      const data = await response.json();
      return data.base64;
    } catch (err) {
      console.warn('无法转换图片:', url, err);
      return null;
    }
  };

  // 导出截图
  const exportScreenshot = async () => {
    if (!previewRef.current) return;

    try {
      // 预处理：将所有图片通过代理转为base64以解决跨域问题
      const images = previewRef.current.querySelectorAll('img');
      const originalSrcs = new Map();
      const convertedImages = [];
      
      // 并行处理所有图片
      const promises = Array.from(images).map(async (img) => {
        if (img.src && !img.src.startsWith('data:')) {
          originalSrcs.set(img, img.src);
          const base64 = await imageToBase64ViaProxy(img.src);
          if (base64) {
            convertedImages.push({ img, base64 });
          }
        }
      });
      
      await Promise.all(promises);
      
      // 应用转换后的图片
      for (const { img, base64 } of convertedImages) {
        img.src = base64;
      }
      
      // 等待图片加载完成
      await new Promise(resolve => setTimeout(resolve, 100));

      // 获取预览区域的实际高度
      const element = previewRef.current;
      const pixelRatio = 2;
      const elementHeight = element.scrollHeight;
      const elementWidth = element.scrollWidth;
      
      // Canvas 最大高度限制（保守值，兼容各浏览器）
      // 注意：这是渲染后的像素高度，考虑 pixelRatio
      const MAX_CANVAS_HEIGHT = 11000;
      const maxElementHeight = Math.floor(MAX_CANVAS_HEIGHT / pixelRatio);
      
      // 使用 html-to-image
      const { toPng } = await import('html-to-image');
      
      if (elementHeight <= maxElementHeight) {
        // 高度在限制内，直接导出
        const dataUrl = await toPng(element, {
          backgroundColor: transparentBg ? null : '#ffffff',
          pixelRatio: pixelRatio,
          skipAutoScale: true,
          filter: (node) => {
            if (node.tagName === 'SCRIPT' || node.tagName === 'NOSCRIPT') {
              return false;
            }
            return true;
          }
        });
        
        // 恢复原始图片src
        for (const [img, src] of originalSrcs) {
          img.src = src;
        }

        const link = document.createElement('a');
        link.download = `gift-screenshot-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } else {
        // 高度超过限制，需要分割截图
        // 先生成完整的大图，然后用 Canvas 裁剪
        const partHeight = maxElementHeight; // 每段的元素高度
        const totalParts = Math.ceil(elementHeight / partHeight);
        
        alert(`截图高度过长(${elementHeight}px)，将自动分割为 ${totalParts} 张图片导出`);
        
        // 先生成完整截图（使用较低的 pixelRatio 避免内存问题）
        const fullDataUrl = await toPng(element, {
          backgroundColor: transparentBg ? null : '#ffffff',
          pixelRatio: 1, // 降低分辨率以支持超长截图
          skipAutoScale: true,
          filter: (node) => {
            if (node.tagName === 'SCRIPT' || node.tagName === 'NOSCRIPT') {
              return false;
            }
            return true;
          }
        });
        
        // 恢复原始图片src
        for (const [img, src] of originalSrcs) {
          img.src = src;
        }
        
        // 加载完整图片
        const fullImage = new Image();
        fullImage.src = fullDataUrl;
        await new Promise((resolve, reject) => {
          fullImage.onload = resolve;
          fullImage.onerror = reject;
        });
        
        // 用 Canvas 裁剪分割
        const fullWidth = fullImage.width;
        const fullHeight = fullImage.height;
        const partPixelHeight = Math.floor(fullHeight / totalParts);
        
        for (let i = 0; i < totalParts; i++) {
          const startY = i * partPixelHeight;
          const currentPartHeight = i === totalParts - 1 
            ? fullHeight - startY 
            : partPixelHeight;
          
          // 创建裁剪 Canvas
          const partCanvas = document.createElement('canvas');
          partCanvas.width = fullWidth;
          partCanvas.height = currentPartHeight;
          const ctx = partCanvas.getContext('2d');
          
          // 裁剪绘制
          ctx.drawImage(
            fullImage,
            0, startY,           // 源图片起点
            fullWidth, currentPartHeight, // 源图片区域大小
            0, 0,                // 目标起点
            fullWidth, currentPartHeight  // 目标大小
          );
          
          const partDataUrl = partCanvas.toDataURL('image/png');
          
          const link = document.createElement('a');
          link.download = `gift-screenshot-${Date.now()}-part${i + 1}.png`;
          link.href = partDataUrl;
          link.click();
          
          // 延迟下载避免浏览器阻止
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (err) {
      console.error('导出截图失败:', err);
      alert('导出截图失败，请重试');
    }
  };

  // 折叠/展开分组
  const toggleExpand = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // 获取当前房间信息
  const getCurrentRoom = () => {
    return rooms.find(r => String(r.uid) === String(selectedRoom));
  };

  // 检查绑定状态 loading
  if (accessStatus.loading) {
    return (
      <div className="gift-screenshot-page">
        <div className="loading">检查权限...</div>
      </div>
    );
  }

  // 没有访问权限，重定向到首页
  if (!accessStatus.hasAccess) {
    navigate('/');
    return null;
  }

  if (loading) {
    return (
      <div className="gift-screenshot-page">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gift-screenshot-page">
        <div className="error">{error}</div>
      </div>
    );
  }

  const currentRoom = getCurrentRoom();
  const hasGuard = availableGifts.guard?.length > 0;
  const hasBlindbox = Object.keys(availableGifts.blindbox || {}).length > 0;
  const hasOther = availableGifts.other?.length > 0;

  return (
    <div className="gift-screenshot-page">
      <BackButton />
      <div className="page-header">
        <h1>📸 礼物截图生成</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/admin/gift-overlay" className="link-btn" style={{ background: '#22c55e' }}>
            📺 OBS 礼物展示
          </Link>
          <Link to="/admin/blindbox" className="link-btn">
            📦 盲盒统计
          </Link>
        </div>
      </div>

      {/* 顶部设置区 */}
      <div className="top-settings section">
        {/* 第一行：房间和时间选择 */}
        <div className="settings-row">
          <div className="setting-item">
            <label>选择房间</label>
            <select
              value={selectedRoom || ''}
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              {rooms.map(room => {
                const displayRoomId = room.anchor_info?.room_id || room.uid;
                const name = room.anchor_info?.uname || `UID: ${room.uid}`;
                return (
                  <option key={room.uid} value={room.uid}>
                    {name} ({displayRoomId})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="setting-item time-range-setting">
            <label>时间范围</label>
            <div className="time-range-buttons compact">
              <button
                className={timeRangeType === 'current' ? 'active' : ''}
                onClick={() => setTimeRangeType('current')}
                disabled={broadcasts.length === 0}
              >
                {broadcasts.length > 0 && broadcasts[0].end_time === null ? '本场' : '最近一场'}
              </button>
              <button
                className={timeRangeType === 'previous' ? 'active' : ''}
                onClick={() => setTimeRangeType('previous')}
                disabled={broadcasts.length < 2}
              >
                上场
              </button>
              <button
                className={timeRangeType === 'today' ? 'active' : ''}
                onClick={() => setTimeRangeType('today')}
              >
                今日
              </button>
              <button
                className={timeRangeType === 'week' ? 'active' : ''}
                onClick={() => setTimeRangeType('week')}
              >
                本周
              </button>
              <button
                className={timeRangeType === 'month' ? 'active' : ''}
                onClick={() => setTimeRangeType('month')}
              >
                本月
              </button>
              <button
                className={timeRangeType === 'custom' ? 'active' : ''}
                onClick={() => setTimeRangeType('custom')}
              >
                自定义
              </button>
            </div>
          </div>
        </div>

        {/* 自定义时间输入 */}
        {timeRangeType === 'custom' && (
          <div className="settings-row custom-time-row">
            <SegmentedTimeInput
              label="开始"
              value={customStartTime}
              onChange={setCustomStartTime}
            />
            <SegmentedTimeInput
              label="结束"
              value={customEndTime}
              onChange={setCustomEndTime}
            />
          </div>
        )}

        {/* 当前时间范围显示 */}
        {startTime && endTime && (
          <div className="time-range-display-top">
            {formatTimestamp(startTime)} ~ {formatTimestamp(endTime)}
          </div>
        )}

        {/* 第二行：显示选项 */}
        <div className="settings-row display-options-row">
          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="mergeGifts"
              checked={mergeGifts}
              onChange={(e) => setMergeGifts(e.target.checked)}
            />
            <label htmlFor="mergeGifts">合并同类礼物</label>
          </div>

          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="showAvatar"
              checked={showAvatar}
              onChange={(e) => setShowAvatar(e.target.checked)}
            />
            <label htmlFor="showAvatar">显示头像</label>
          </div>

          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="useNickname"
              checked={useNickname}
              onChange={(e) => setUseNickname(e.target.checked)}
            />
            <label htmlFor="useNickname">使用自定义昵称</label>
          </div>

          <div className="setting-item checkbox-item">
            <input
              type="checkbox"
              id="transparentBg"
              checked={transparentBg}
              onChange={(e) => setTransparentBg(e.target.checked)}
            />
            <label htmlFor="transparentBg">透明背景</label>
          </div>

          <div className="setting-item">
            <label>排序</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="time">按时间</option>
              <option value="value">按价值</option>
              <option value="count">按数量</option>
            </select>
          </div>

          <div className="setting-item">
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="desc">降序</option>
              <option value="asc">升序</option>
            </select>
          </div>

          <div className="setting-item value-filter">
            <label>价值(元)</label>
            <input
              type="number"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
              placeholder="最小"
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              placeholder="最大"
              min="0"
            />
          </div>
        </div>
      </div>

      <div className="main-layout">
        {/* 左侧预览区 */}
        <div className="left-panel">
          <div className="section preview-section">
            <h2 className="section-title">预览</h2>
            <div 
              className={`preview-container ${transparentBg ? 'transparent-bg' : 'white-bg'}`}
            >
              <div 
                ref={previewRef}
                className="gift-preview"
                style={{ '--theme-color': themeColor }}
              >
                {dataLoading ? (
                  <div className="preview-loading">加载中...</div>
                ) : gifts.length === 0 ? (
                  <div className="preview-empty">暂无礼物数据</div>
                ) : (
                  <div className="gift-list">
                    {gifts.map((gift, index) => {
                      // 舰长图标使用B站官方CDN
                      const guardIconMap = {
                        '舰长': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/captain-Bjw5Byb5.png',
                        '提督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/supervisor-u43ElIjU.png',
                        '总督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/governor-DpDXKEdA.png'
                      };
                      const giftIconUrl = gift.img_basic || guardIconMap[gift.gift_name];
                      const guardFrameUrl = guardFrameMap[userGuardLevels[gift.uid]];

                      return (
                        <div
                          key={gift.id || `${gift.gift_id}-${gift.uid}-${index}`}
                          className={`bubble-wrapper ${getGiftLevelClass(gift.num * gift.price)}`}
                        >
                          <div className="bubble-bg"></div>
                          <div className="bubble-content">
                            {showAvatar && (
                              <div className="avatar-wrapper">
                                {userFaces[gift.uid] ? (
                                  <img
                                    className="user-avatar"
                                    src={userFaces[gift.uid]}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    crossOrigin="anonymous"
                                  />
                                ) : (
                                  <div className="avatar-placeholder" />
                                )}
                                {guardFrameUrl && (
                                  <img
                                    className="guard-frame"
                                    src={guardFrameUrl}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                  />
                                )}
                              </div>
                            )}
                            <div className="gift-info">
                              <div className="info-row">
                                <span className="user-name">{gift.username}</span>
                                <span className="action-text">投喂</span>
                              </div>
                              <span className="gift-name">{gift.gift_name}</span>
                            </div>
                            {giftIconUrl && (
                              <img
                                src={giftIconUrl}
                                alt={gift.gift_name}
                                className="gift-icon"
                                referrerPolicy="no-referrer"
                              />
                            )}
                            <div className="combo-count">
                              <div className="count-part part-x" data-text="x">
                                <span className="gradient-text">x</span>
                              </div>
                              <div className="count-part part-num" data-text={String(gift.num || 1)}>
                                <span className="gradient-text">{gift.num || 1}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* 统计信息 */}
            <div className="summary-info">
              <span>共 {summary.total_records || 0} 条记录</span>
              <span>总价值 ￥{formatValue(summary.total_value)}</span>
              <span>{summary.unique_users || 0} 位用户</span>
            </div>

            {/* 导出按钮 */}
            <button 
              className="export-btn"
              onClick={exportScreenshot}
              disabled={gifts.length === 0}
            >
              导出透明截图
            </button>
          </div>
        </div>

        {/* 右侧配置区 */}
        <div className="right-panel">
          {/* 礼物选择 */}
          <div className="section">
            <h3 className="section-title">
              礼物选择
              {giftsLoading && <span className="loading-hint">加载中...</span>}
            </h3>

            <div className="quick-actions">
              <button onClick={selectAllGifts}>全选</button>
              <button onClick={deselectAllGifts}>取消全选</button>
              <button onClick={loadGiftData} disabled={dataLoading}>
                刷新数据
              </button>
            </div>

            {/* 大航海分组 */}
            {hasGuard && (
              <div className="gift-group">
                <div 
                  className="group-header"
                  onClick={() => toggleExpand('guard')}
                >
                  <span className="expand-icon">{expandedGroups.guard ? '▼' : '▶'}</span>
                  <span className="group-name">大航海</span>
                  <button 
                    className="group-toggle-btn"
                    onClick={(e) => { e.stopPropagation(); toggleGroup('guard'); }}
                  >
                    {availableGifts.guard.every(g => selectedGiftIds.has(g.gift_id)) ? '取消' : '全选'}
                  </button>
                </div>
                {expandedGroups.guard && (
                  <div className="gift-items">
                    {availableGifts.guard.map(gift => {
                      // 舰长图标使用B站官方CDN
                      const guardIconMap = {
                        '舰长': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/captain-Bjw5Byb5.png',
                        '提督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/supervisor-u43ElIjU.png',
                        '总督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/governor-DpDXKEdA.png'
                      };
                      const iconUrl = gift.img_basic || guardIconMap[gift.gift_name];
                      return (
                        <label key={`guard-${gift.gift_id}`} className="gift-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedGiftIds.has(gift.gift_id)}
                            onChange={() => toggleGift(gift.gift_id)}
                          />
                          {iconUrl && (
                            <img src={iconUrl} alt="" className="gift-item-icon" referrerPolicy="no-referrer" />
                          )}
                          <span className="gift-label">
                            {gift.gift_name}
                            <span className="gift-stats">({gift.total_num}个)</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 盲盒分组 */}
            {hasBlindbox && Object.entries(availableGifts.blindbox).map(([boxName, boxGifts]) => (
              <div key={boxName} className="gift-group">
                <div 
                  className="group-header"
                  onClick={() => toggleExpand(`blindbox-${boxName}`)}
                >
                  <span className="expand-icon">
                    {expandedGroups[`blindbox-${boxName}`] !== false ? '▼' : '▶'}
                  </span>
                  <span className="group-name">{boxName}</span>
                  <button 
                    className="group-toggle-btn"
                    onClick={(e) => { e.stopPropagation(); toggleGroup('blindbox', boxName); }}
                  >
                    {boxGifts.every(g => selectedGiftIds.has(g.gift_id)) ? '取消' : '全选'}
                  </button>
                </div>
                {expandedGroups[`blindbox-${boxName}`] !== false && (
                  <div className="gift-items">
                    {boxGifts.map((gift, idx) => (
                      <label key={`${boxName}-${gift.gift_id}-${idx}`} className="gift-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedGiftIds.has(gift.gift_id)}
                          onChange={() => toggleGift(gift.gift_id)}
                        />
                        {gift.img_basic && (
                          <img src={gift.img_basic} alt="" className="gift-item-icon" referrerPolicy="no-referrer" />
                        )}
                        <span className="gift-label">
                          {gift.gift_name}
                          <span className="gift-stats">({gift.total_num}个)</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* 其他礼物分组 */}
            {hasOther && (
              <div className="gift-group">
                <div 
                  className="group-header"
                  onClick={() => toggleExpand('other')}
                >
                  <span className="expand-icon">{expandedGroups.other ? '▼' : '▶'}</span>
                  <span className="group-name">其他礼物</span>
                  <button 
                    className="group-toggle-btn"
                    onClick={(e) => { e.stopPropagation(); toggleGroup('other'); }}
                  >
                    {availableGifts.other.every(g => selectedGiftIds.has(g.gift_id)) ? '取消' : '全选'}
                  </button>
                </div>
                {expandedGroups.other && (
                  <div className="gift-items">
                    {availableGifts.other.map((gift, idx) => (
                      <label key={`other-${gift.gift_id}-${idx}`} className="gift-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedGiftIds.has(gift.gift_id)}
                          onChange={() => toggleGift(gift.gift_id)}
                        />
                        {gift.img_basic && (
                          <img src={gift.img_basic} alt="" className="gift-item-icon" referrerPolicy="no-referrer" />
                        )}
                        <span className="gift-label">
                          {gift.gift_name}
                          <span className="gift-stats">({gift.total_num}个)</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!hasGuard && !hasBlindbox && !hasOther && !giftsLoading && (
              <div className="no-gifts">该时间范围内没有礼物记录</div>
            )}
          </div>

          {/* 用户筛选 */}
          <div className="section">
            <h3 className="section-title">用户筛选（可选）</h3>
            <p className="section-hint">不选择则显示所有用户的礼物</p>
            
            <div className="user-list">
              {activeUsers.map(user => (
                <label key={user.uid} className="user-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.has(user.uid)}
                    onChange={() => toggleUser(user.uid)}
                  />
                  <span className="user-label">
                    {user.username}
                    <span className="user-stats">￥{formatValue(user.total_value)}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiftScreenshot;
